import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useGarunteedLocalStorageState } from "../hooks";
import { ArtData, SITE_DATA_PARTITIONS } from "../../../types";
import { isSameArt } from "../utils/art";
import generators from "../../../shared-utils/noDep/generators";
import { CartData } from "../../../types/cart";
import { LocationData } from "../../../types/location";

type AddItemInput = ArtData;
type RemoveItemInput = Pick<ArtData, "slug">;

type Context =
  | {
      cart: CartData & {
        locationData?: LocationData;
        locationUpdatedAt?: string;
      };
      addItem: (item: AddItemInput) => void;
      removeItem: (item: RemoveItemInput) => void;
      setCart: Dispatch<SetStateAction<CartData>>;
      resetCart: () => void;
    }
  | undefined;
const cartContext = createContext<Context>(undefined);
const createCart = () => ({
  type: SITE_DATA_PARTITIONS.CART,
  date: new Date().toISOString(),
  items: [],
  slug: generators.randomNumberString(12),
});
export const CartProvider = ({ children }: { children: JSX.Element }) => {
  const [cart, setCart] = useGarunteedLocalStorageState<
    NonNullable<Context>["cart"]
  >("cart", createCart());

  const { addItem, removeItem, resetCart } = useMemo(() => {
    return {
      addItem: (artItem: AddItemInput) => {
        setCart((cart) => ({
          ...cart,
          items: [
            ...cart.items.filter((item) => !isSameArt(item, artItem)),
            artItem,
          ],
        }));
      },
      removeItem: (artItem: RemoveItemInput) => {
        setCart((cart) => ({
          ...cart,
          items: [...cart.items.filter((item) => !isSameArt(item, artItem))],
        }));
      },
      resetCart: () => {
        setCart(createCart());
      },
    };
  }, [setCart]);
  return (
    <cartContext.Provider
      value={{ cart, addItem, removeItem, resetCart, setCart }}
    >
      <>{children}</>
    </cartContext.Provider>
  );
};
export const useCart = () =>
  useContext(cartContext) ?? ({} as NonNullable<Context>);
